/**
 * @file     StatGridBlock.tsx
 * @purpose  Renders a content-engine stat_grid block as editorial stat tiles separated by hairlines.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';

type StatGridBlockProps = Extract<ContentBlock, { t: 'stat_grid' }>;

export const StatGridBlock = ({ caption, stats }: StatGridBlockProps) => {
  const { theme } = useAppTheme();

  if (stats.length === 0) return null;

  return (
    <Box component="section">
      {caption ? (
        <Typography
          component="h2"
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.bodyLg,
            fontWeight: 600,
            color: theme.semanticColors.textPrimary,
            mb: '16px',
          }}
        >
          {caption}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          borderTop: `1px solid ${theme.semanticColors.borderSubtle}`,
          borderLeft: `1px solid ${theme.semanticColors.borderSubtle}`,
        }}
      >
        {stats.map((stat) => (
          <Box
            key={`${stat.label}-${stat.value}`}
            sx={{
              p: { xs: '14px 16px', md: '18px 20px' },
              minHeight: 96,
              borderRight: `1px solid ${theme.semanticColors.borderSubtle}`,
              borderBottom: `1px solid ${theme.semanticColors.borderSubtle}`,
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.caption,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: theme.semanticColors.textMuted,
                mb: '6px',
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamilyHeading,
                fontSize: { xs: theme.typography.bodyLg, md: theme.typography.h4 },
                fontWeight: 600,
                lineHeight: 1.15,
                color: theme.semanticColors.textPrimary,
              }}
            >
              {stat.value}
            </Typography>
            {stat.note ? (
              <Typography
                sx={{
                  fontSize: theme.typography.caption,
                  color: theme.semanticColors.textMuted,
                  lineHeight: 1.45,
                  mt: '6px',
                }}
              >
                {stat.note}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
